import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  Bell,
  Bluetooth,
  Braces,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Database,
  Gauge,
  Power,
  RefreshCw,
  RotateCw,
  Satellite,
  UploadCloud,
} from 'lucide-react';
import {
  getDeviceStatusEntry,
  getTaskInfo,
  getMemInfo,
  getBluetoothInfo,
  sendDeviceCommand,
  sendCanFrames,
  checkIsOnline,
  getAllMachines,
  getMachineImuInstallations,
  getDeviceNotifications,
  getIncidents,
  solveAllIncidents,
} from '../api/api';
import { buildGnssUpgradeCommand, listGnssFirmwares } from '../api/GnssOtaApi';
import ImuDiagnosticsPanel from '../components/status/ImuDiagnosticsPanel';
import NetworkUsagePanel from '../components/status/NetworkUsagePanel';
import StatusCard from '../components/status/StatusCard';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import { formatTimestamp, formatRelativeTime, formatUptime } from '../utils/dateUtils';
import { getGnssUpgrading, getHubGnssType, parseGPGGA, parseGNGST } from '../utils/gnssUtils';
import { hasFullAccess, isAdmin } from '../utils/auth';
import { fixCanStringsChecksums, parseCanStrings } from '../utils/canStringUtils';
import {
  formatCanImuIdListWithPlaceStatus,
  getCanPortFromImuFieldKey,
  groupImuInstallationsByCanPort,
} from '../utils/imuIdUtils';
import {
  fieldOrder,
  formatMemInfo,
  formatTaskInfoText,
  getTaskInfoTimestamps,
  parseTwaiState,
  TWAI_STATE_NAMES,
} from '../utils/statusUtils';
import styles from './DeviceStatus.module.scss';

// Bounds accepted by the hub firmware for the canbus_N_log_S command.
const CAN_LOG_MIN_SECONDS = 1;
const CAN_LOG_MAX_SECONDS = 600;
const CAN_LOG_BUSES = [0, 1, 2];
// Bounds accepted by the hub firmware for the gnss_log_S command.
const GNSS_LOG_MIN_SECONDS = 1;
const GNSS_LOG_MAX_SECONDS = 600;
// The hub streams GNSS firmware into the receiver UART at 115200 baud (8N1),
// i.e. ~11.5 KB/s or roughly 1.5 minutes per MB.
const GNSS_UPGRADE_BYTES_PER_SECOND = 115200 / 10;
const CAN_TX_PLACEHOLDER = [
  '$CAN,26492,080EFF7E,0,8F812902BA5F6E8B*4F',
  '$CAN,26492,0CFDD67E,0,01000100FFFFFFFF*36',
  '$CAN,26502,080EFF91,0,5F6E29029DA13AAF*4B',
].join('\n');
const NOTIFICATION_PAGE_SIZE = 20;
const RECENT_INCIDENT_COUNT = 10;

// "YYYY-MM-DD HH:MM" in local time; the tooltip carries seconds and timezone.
const formatLocalDateTime = timestamp => formatTimestamp(timestamp).slice(0, 16);

const statusViews = [
  { id: 'overview', label: 'Status overview', icon: Activity },
  { id: 'system', label: 'System diagnostics', icon: Cpu },
  { id: 'bluetooth', label: 'Bluetooth', icon: Bluetooth },
  { id: 'network', label: 'Network usage', icon: Gauge },
  { id: 'raw', label: 'Raw data', icon: Braces },
];

function DeviceStatus() {
  const { serialNumber } = useParams();
  const selectedDevice = serialNumber;
  const [statusData, setStatusData] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [previousParsedData, setPreviousParsedData] = useState(null);
  const [statusTimestamps, setStatusTimestamps] = useState([]);
  const [selectedStatusTimestamp, setSelectedStatusTimestamp] = useState('latest');
  const [error, setError] = useState('');
  const [commandStatus, setCommandStatus] = useState('');
  const [canLogBus, setCanLogBus] = useState('0');
  const [canLogSeconds, setCanLogSeconds] = useState('30');
  const [gnssLogSeconds, setGnssLogSeconds] = useState('30');
  const [canTxBus, setCanTxBus] = useState('0');
  const [canTxOpen, setCanTxOpen] = useState(false);
  const [canTxText, setCanTxText] = useState('');
  const [canTxError, setCanTxError] = useState('');
  const [canTxSending, setCanTxSending] = useState(false);
  const [selectedTaskInfoTimestamp, setSelectedTaskInfoTimestamp] = useState('');
  const [isDeviceOnline, setIsDeviceOnline] = useState(true);
  const [taskInfo, setTaskInfo] = useState(null);
  const [memInfo, setMemInfo] = useState(null);
  const [bluetoothInfo, setBluetoothInfo] = useState(null);
  const [imuInstallationsByCan, setImuInstallationsByCan] = useState({});
  const [activeView, setActiveView] = useState('overview');
  const [pendingCommand, setPendingCommand] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Bumped by "Refresh all" so self-loading panels fetch again
  const [refreshToken, setRefreshToken] = useState(0);
  const [gnssUpgradeOpen, setGnssUpgradeOpen] = useState(false);
  const [gnssFirmwares, setGnssFirmwares] = useState(null); // null while loading
  const [gnssFirmwareError, setGnssFirmwareError] = useState('');
  const [gnssUpgradeId, setGnssUpgradeId] = useState('');
  const [notifications, setNotifications] = useState(null); // null while loading
  const [notificationPagination, setNotificationPagination] = useState(null);
  const [notificationPage, setNotificationPage] = useState(1);
  // undefined until the machine lookup finishes, null when the hub has no machine
  const [machineId, setMachineId] = useState(undefined);
  const [incidents, setIncidents] = useState(null); // null while loading
  const [unsolvedIncidentCount, setUnsolvedIncidentCount] = useState(0);
  const [confirmSolveAll, setConfirmSolveAll] = useState(false);
  const [solvingAll, setSolvingAll] = useState(false);
  const [incidentNotice, setIncidentNotice] = useState('');

  const fetchImuInstallations = useCallback(async () => {
    if (!selectedDevice) {
      setImuInstallationsByCan({});
      setMachineId(null);
      return;
    }

    try {
      const machinesResponse = await getAllMachines();
      const currentMachine = machinesResponse.data.find(
        machine => machine.serial === selectedDevice
      );

      if (!currentMachine) {
        setImuInstallationsByCan({});
        setMachineId(null);
        return;
      }
      setMachineId(currentMachine.id);

      const installationsResponse = await getMachineImuInstallations(currentMachine.id);
      const installations = installationsResponse.data?.installations || [];
      setImuInstallationsByCan(groupImuInstallationsByCanPort(installations));
    } catch (error) {
      console.error('Error fetching IMU installations:', error);
      setImuInstallationsByCan({});
      setMachineId(currentId => (currentId === undefined ? null : currentId));
    }
  }, [selectedDevice]);

  // Load only the latest status entry (plus the list of available
  // timestamps) instead of downloading the whole history.
  const fetchStatus = useCallback(async () => {
    try {
      setError('');
      const [entryResponse, onlineResponse] = await Promise.all([
        getDeviceStatusEntry(selectedDevice, 'latest'),
        checkIsOnline(selectedDevice),
      ]);

      const { entry, previous_entry: previousEntry, timestamps } = entryResponse.data;
      setStatusTimestamps(timestamps || []);
      setSelectedStatusTimestamp('latest');
      setParsedData(entry);
      setPreviousParsedData(previousEntry || null);
      setStatusData(JSON.stringify(entry, null, 2));

      setIsDeviceOnline(onlineResponse.data.is_online);
      setSelectedTaskInfoTimestamp('');
    } catch (error) {
      console.error('Error fetching status:', error);
      setError('Failed to fetch device status');
      setStatusData('');
      setParsedData(null);
      setPreviousParsedData(null);
      setStatusTimestamps([]);
      setSelectedStatusTimestamp('latest');
      setSelectedTaskInfoTimestamp('');
      setIsDeviceOnline(false);
    }
  }, [selectedDevice]);

  // Fetch a single historical entry when the user picks a timestamp.
  const handleStatusTimestampChange = async value => {
    setSelectedStatusTimestamp(value);
    try {
      const response = await getDeviceStatusEntry(selectedDevice, value);
      const { entry, previous_entry: previousEntry } = response.data;
      setParsedData(entry);
      setPreviousParsedData(previousEntry || null);
      setStatusData(JSON.stringify(entry, null, 2));
    } catch (error) {
      console.error('Error fetching status entry:', error);
      setError('Failed to fetch status entry');
    }
  };

  useEffect(() => {
    if (selectedDevice) {
      fetchStatus();
    }
  }, [selectedDevice, fetchStatus]);

  useEffect(() => {
    fetchImuInstallations();
  }, [fetchImuInstallations]);

  const fetchNotifications = useCallback(async () => {
    if (!selectedDevice) {
      return;
    }
    try {
      const response = await getDeviceNotifications(
        selectedDevice,
        notificationPage,
        NOTIFICATION_PAGE_SIZE
      );
      setNotifications(response.data.notifications || []);
      setNotificationPagination(response.data.pagination || null);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setNotificationPagination(null);
    }
  }, [selectedDevice, notificationPage]);

  // Start from the newest notifications and drop the previous hub's
  // incidents whenever another hub is opened.
  useEffect(() => {
    setNotificationPage(1);
    setMachineId(undefined);
    setIncidents(null);
  }, [selectedDevice]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const fetchIncidents = useCallback(async () => {
    if (machineId === undefined) {
      return;
    }
    if (machineId === null) {
      setIncidents([]);
      setUnsolvedIncidentCount(0);
      return;
    }
    try {
      const response = await getIncidents(1, RECENT_INCIDENT_COUNT, 'unsolved', machineId);
      setIncidents(response.data?.incidents || []);
      setUnsolvedIncidentCount(response.data?.pagination?.total_count || 0);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      setIncidents([]);
      setUnsolvedIncidentCount(0);
    }
  }, [machineId]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleSolveAllIncidents = async () => {
    try {
      setSolvingAll(true);
      const response = await solveAllIncidents(machineId);
      setConfirmSolveAll(false);
      setIncidentNotice(`${response.data?.updated_count ?? 0} incidents marked solved.`);
      await fetchIncidents();
    } catch (error) {
      console.error('Error solving incidents:', error);
      setConfirmSolveAll(false);
      setError(error.response?.data?.detail || 'Failed to solve all incidents');
    } finally {
      setSolvingAll(false);
    }
  };

  const refreshAllData = useCallback(async () => {
    setRefreshing(true);
    setRefreshToken(token => token + 1);
    await Promise.all([
      fetchStatus(),
      fetchImuInstallations(),
      fetchNotifications(),
      fetchIncidents(),
    ]);
    // Re-fetch task info, mem info, and bluetooth info
    const fetchTaskInfo = async () => {
      try {
        const response = await getTaskInfo(selectedDevice);
        setTaskInfo(response.data);
      } catch (error) {
        console.error('Error fetching task info:', error);
        setTaskInfo(null);
      }
    };
    const fetchMemInfo = async () => {
      try {
        const response = await getMemInfo(selectedDevice);
        setMemInfo(response.data);
      } catch (error) {
        console.error('Error fetching mem info:', error);
        setMemInfo(null);
      }
    };
    const fetchBluetoothInfo = async () => {
      try {
        const response = await getBluetoothInfo(selectedDevice);
        setBluetoothInfo(response.data);
      } catch (error) {
        console.error('Error fetching bluetooth info:', error);
        setBluetoothInfo(null);
      }
    };
    await Promise.all([fetchTaskInfo(), fetchMemInfo(), fetchBluetoothInfo()]);
    setRefreshing(false);
  }, [selectedDevice, fetchStatus, fetchImuInstallations, fetchNotifications, fetchIncidents]);

  // Fetch task info separately
  useEffect(() => {
    const fetchTaskInfo = async () => {
      if (!selectedDevice) {
        return;
      }
      try {
        const response = await getTaskInfo(selectedDevice);
        setTaskInfo(response.data);
      } catch (error) {
        console.error('Error fetching task info:', error);
        setTaskInfo(null);
      }
    };

    fetchTaskInfo();
  }, [selectedDevice]);

  // Fetch mem info separately
  useEffect(() => {
    const fetchMemInfo = async () => {
      if (!selectedDevice) {
        return;
      }
      try {
        const response = await getMemInfo(selectedDevice);
        setMemInfo(response.data);
      } catch (error) {
        console.error('Error fetching mem info:', error);
        setMemInfo(null);
      }
    };

    fetchMemInfo();
  }, [selectedDevice]);

  // Fetch bluetooth info separately
  useEffect(() => {
    const fetchBluetoothInfo = async () => {
      if (!selectedDevice) {
        return;
      }
      try {
        const response = await getBluetoothInfo(selectedDevice);
        setBluetoothInfo(response.data);
      } catch (error) {
        console.error('Error fetching bluetooth info:', error);
        setBluetoothInfo(null);
      }
    };

    fetchBluetoothInfo();
  }, [selectedDevice]);

  // Set default selected timestamp for task info
  useEffect(() => {
    if (taskInfo && !selectedTaskInfoTimestamp) {
      const timestamps = Object.keys(taskInfo);
      if (timestamps.length > 0) {
        const newestTimestamp = timestamps.reduce((max, current) =>
          parseInt(current) > parseInt(max) ? current : max
        );
        setSelectedTaskInfoTimestamp(newestTimestamp);
      }
    }
  }, [taskInfo, selectedTaskInfoTimestamp]);

  const handleSendCommand = async command => {
    try {
      setCommandStatus(`Sending ${command} command...`);
      const response = await sendDeviceCommand(selectedDevice, command);
      setCommandStatus(`Success: ${response.data.message}`);
      // Refresh all data after command execution
      setTimeout(refreshAllData, 2000);
    } catch (error) {
      console.error('Error sending command:', error);
      setCommandStatus(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUnpairDevice = device => {
    const macAddress = device.address.toLowerCase().replace(/:/g, '');
    setPendingCommand({
      command: `ble_del_${macAddress}_${device.type}`,
      title: `Unpair ${device.address}?`,
      description: 'The device must be paired again before it can reconnect.',
    });
  };

  const requestCommand = (command, title, description) => {
    setPendingCommand({ command, title, description });
  };

  const confirmCommand = async () => {
    if (!pendingCommand) {
      return;
    }
    const command = pendingCommand.command;
    setPendingCommand(null);
    await handleSendCommand(command);
  };

  const handleStartCanLog = event => {
    event.preventDefault();
    const seconds = Number(canLogSeconds);
    if (
      !Number.isInteger(seconds) ||
      seconds < CAN_LOG_MIN_SECONDS ||
      seconds > CAN_LOG_MAX_SECONDS
    ) {
      setCommandStatus(
        `Error: Log duration must be a whole number between ${CAN_LOG_MIN_SECONDS} and ${CAN_LOG_MAX_SECONDS} seconds`
      );
      return;
    }
    handleSendCommand(`canbus_${canLogBus}_log_${seconds}`);
  };

  const handleStartGnssLog = event => {
    event.preventDefault();
    const seconds = Number(gnssLogSeconds);
    if (
      !Number.isInteger(seconds) ||
      seconds < GNSS_LOG_MIN_SECONDS ||
      seconds > GNSS_LOG_MAX_SECONDS
    ) {
      setCommandStatus(
        `Error: Log duration must be a whole number between ${GNSS_LOG_MIN_SECONDS} and ${GNSS_LOG_MAX_SECONDS} seconds`
      );
      return;
    }
    handleSendCommand(`gnss_log_${seconds}`);
  };

  // GNSS receiver firmware upgrade is an OTA feature, hidden from the partner role
  const canUpgradeGnss = hasFullAccess();
  // Only take up room for incidents when there is something to act on; the
  // notifications panel spreads into the freed column otherwise.
  const showIncidents = Boolean(machineId) && incidents?.length > 0;
  const hubGnssType = getHubGnssType(parsedData);
  const gnssUpgrading = getGnssUpgrading(parsedData);
  const selectedGnssFirmware =
    (gnssFirmwares || []).find(firmware => String(firmware.id) === gnssUpgradeId) || null;

  // Load firmware files for the hub's GNSS type each time the dialog opens
  useEffect(() => {
    if (!gnssUpgradeOpen || !hubGnssType) {
      return undefined;
    }
    let cancelled = false;
    setGnssFirmwares(null);
    setGnssFirmwareError('');
    listGnssFirmwares(hubGnssType)
      .then(response => {
        if (!cancelled) {
          const wantedType = hubGnssType.toLowerCase();
          setGnssFirmwares(
            (response.data || []).filter(
              firmware => (firmware.gnss_type || '').toLowerCase() === wantedType
            )
          );
        }
      })
      .catch(error => {
        if (!cancelled) {
          setGnssFirmwares([]);
          setGnssFirmwareError(
            `Could not load GNSS firmware list: ${error.response?.data?.detail || error.message}`
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gnssUpgradeOpen, hubGnssType]);

  const openGnssUpgrade = () => {
    setGnssUpgradeId('');
    setGnssFirmwareError('');
    setGnssUpgradeOpen(true);
  };

  const closeGnssUpgrade = () => setGnssUpgradeOpen(false);

  const confirmGnssUpgrade = async () => {
    if (!selectedGnssFirmware) {
      return;
    }
    // Use the type string from the firmware row so the hub's download request
    // matches the stored (gnss_type, version) exactly.
    const command = buildGnssUpgradeCommand(
      selectedGnssFirmware.gnss_type,
      selectedGnssFirmware.version
    );
    setGnssUpgradeOpen(false);
    await handleSendCommand(command);
  };

  const formatFirmwareSize = bytes =>
    typeof bytes === 'number' ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '—';

  const estimateGnssUpgradeMinutes = bytes =>
    Math.max(1, Math.ceil(bytes / GNSS_UPGRADE_BYTES_PER_SECOND / 60));

  const canTxParsed = parseCanStrings(canTxText);

  const closeCanTx = () => {
    if (!canTxSending) {
      setCanTxOpen(false);
    }
  };

  const handleFixCanCrc = () => {
    setCanTxText(fixCanStringsChecksums(canTxText));
    setCanTxError('');
  };

  const handleSendCanFrames = async () => {
    const { frames, errors } = canTxParsed;
    if (errors.length > 0) {
      setCanTxError(`Line ${errors[0].line}: ${errors[0].message}`);
      return;
    }
    if (frames.length === 0) {
      setCanTxError('Enter at least one CAN string.');
      return;
    }

    setCanTxSending(true);
    setCanTxError('');
    try {
      const response = await sendCanFrames(selectedDevice, Number(canTxBus), frames);
      setCommandStatus(`Success: ${response.data.message}`);
      setCanTxOpen(false);
      setTimeout(refreshAllData, 2000);
    } catch (error) {
      console.error('Error sending CAN frames:', error);
      const detail = error.response?.data?.detail;
      setCanTxError(typeof detail === 'string' ? detail : error.message);
    } finally {
      setCanTxSending(false);
    }
  };

  const isCanPortPowered = canPort => {
    if (!parsedData) {
      return true;
    }

    const powerFieldKey = `can_${canPort}_power`;
    if (!Object.prototype.hasOwnProperty.call(parsedData, powerFieldKey)) {
      return true;
    }

    return parsedData[powerFieldKey] === true;
  };

  const formatFieldValue = (key, value) => {
    const canPort = getCanPortFromImuFieldKey(key);
    if (canPort !== null) {
      const expectedInstallations = isCanPortPowered(canPort)
        ? imuInstallationsByCan[canPort] || []
        : [];
      return formatCanImuIdListWithPlaceStatus(value, expectedInstallations);
    }

    if (key === 'ntp_time' && typeof value === 'number') {
      return formatTimestamp(value);
    } else if (key === 'ntp_last_updated' && typeof value === 'number') {
      return value < 100000 ? '-' : `${formatTimestamp(value)} ${formatRelativeTime(value)}`;
    } else if (key === 'last_update_time' && typeof value === 'number') {
      return `${formatTimestamp(value)}\n(${formatRelativeTime(value)})`;
    } else if (key === 'uptime' && typeof value === 'number') {
      return formatUptime(value);
    } else if (key === 'gnss_last_location' && typeof value === 'string') {
      return parseGPGGA(value);
    } else if (key === 'gnss_last_gst' && typeof value === 'string') {
      return parseGNGST(value);
    } else if (key === 'can_0_state') {
      const state = parseTwaiState(value);
      return state === null ? String(value) : `${value} (${TWAI_STATE_NAMES[state]})`;
    } else if (key === 'modem_rssi') {
      const rssi = parseInt(value);
      if (isNaN(rssi) || rssi < 0 || rssi > 32) {
        return String(value);
      }
      const dBm = -113 + 2 * rssi;
      return `${rssi} (${dBm} dBm)`;
    } else if (key === 'modem_network_info' && typeof value === 'string') {
      const parts = value.split(',');
      if (parts.length !== 4) {
        return String(value);
      }
      return `Network Type: ${parts[0].trim()}\nBand: ${parts[2].trim()}\nChannel: ${parts[3].trim()}\nOperator Code: ${parts[1].trim()}`;
    } else if (typeof value === 'object') {
      if (value === null) {
        return 'null';
      }
      try {
        const seen = new WeakSet();
        return JSON.stringify(value, (key, val) => {
          if (val !== null && typeof val === 'object') {
            if (seen.has(val)) {
              return '[Circular]';
            }
            seen.add(val);
          }
          return val;
        });
      } catch (error) {
        return '[Object - cannot stringify]';
      }
    } else {
      return String(value);
    }
  };

  const renderStatusCards = () => {
    if (!parsedData) {
      return null;
    }

    const orderedFields = fieldOrder
      .filter(key => Object.prototype.hasOwnProperty.call(parsedData, key))
      .concat(
        Object.keys(parsedData).filter(
          key =>
            !fieldOrder.includes(key) && key !== 'task_info' && key !== 'mem_info' && key !== 'imus'
        )
      );

    return orderedFields.map(key => (
      <StatusCard
        key={key}
        fieldKey={key}
        value={formatFieldValue(key, parsedData[key])}
        isDeviceOnline={isDeviceOnline}
        fullData={parsedData}
        previousData={previousParsedData}
      />
    ));
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Live hub telemetry</span>
          <h1>Hub health</h1>
          <p>
            Inspect current telemetry first; open diagnostics only when deeper investigation is
            needed.
          </p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={refreshAllData}
          disabled={refreshing}
        >
          <RefreshCw size={16} className={refreshing ? styles.spinning : ''} />
          {refreshing ? 'Refreshing' : 'Refresh all'}
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {incidentNotice && (
        <Toast tone="success" onClose={() => setIncidentNotice('')}>
          {incidentNotice}
        </Toast>
      )}
      {commandStatus && (
        <Toast
          tone={commandStatus.startsWith('Error') ? 'error' : 'info'}
          onClose={() => setCommandStatus('')}
        >
          {commandStatus}
        </Toast>
      )}

      <div className={styles.commandRow}>
        <section className={styles.commandCenter}>
          <div className={styles.commandHeader}>
            <div>
              <span>Remote commands</span>
              <h2>Command center</h2>
            </div>
            <span
              className={`${styles.connectionBadge} ${isDeviceOnline ? styles.onlineBadge : styles.offlineBadge}`}
            >
              <i />
              {isDeviceOnline ? 'Hub online' : 'Hub offline'}
            </span>
          </div>
          <div className={styles.commandLayout}>
            <div className={styles.quickActions}>
              <div className={styles.commandGroup}>
                <span className={styles.groupLabel}>Common tasks</span>
                <button type="button" onClick={() => handleSendCommand('syncall')}>
                  <RotateCw size={17} />
                  <span>
                    <strong>Synchronize now</strong>
                    <small>Run a full hub synchronization.</small>
                  </span>
                </button>
                <button type="button" onClick={() => handleSendCommand('upload_local_config')}>
                  <UploadCloud size={17} />
                  <span>
                    <strong>Upload local configs</strong>
                    <small>Send hub-side configuration to cloud.</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.rebootAction}
                  onClick={() =>
                    requestCommand(
                      'reboot',
                      'Reboot this hub?',
                      'The hub will be unavailable while it restarts.'
                    )
                  }
                >
                  <Power size={17} />
                  <span>
                    <strong>Reboot hub</strong>
                    <small>Restart the device remotely.</small>
                  </span>
                </button>
              </div>
              <div className={styles.commandGroup}>
                <span className={`${styles.groupLabel} ${styles.groupLabelSpaced}`}>
                  GNSS logging
                </span>
                <form className={styles.canLogCard} onSubmit={handleStartGnssLog}>
                  <span>
                    <strong>Log GNSS data</strong>
                    <small>
                      Temporarily record raw GNSS output on the hub for the given duration.
                    </small>
                  </span>
                  <div className={`${styles.canLogForm} ${styles.gnssLogForm}`}>
                    <label>
                      <span>Seconds</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={GNSS_LOG_MIN_SECONDS}
                        max={GNSS_LOG_MAX_SECONDS}
                        step={1}
                        value={gnssLogSeconds}
                        onChange={e => setGnssLogSeconds(e.target.value)}
                      />
                    </label>
                    <button type="submit">Start log</button>
                  </div>
                </form>
                {canUpgradeGnss && (
                  <>
                    <span className={`${styles.groupLabel} ${styles.groupLabelSpaced}`}>
                      GNSS receiver firmware
                    </span>
                    <button
                      type="button"
                      onClick={openGnssUpgrade}
                      disabled={!isDeviceOnline || Boolean(gnssUpgrading)}
                      title={
                        !isDeviceOnline
                          ? 'Hub is offline'
                          : gnssUpgrading
                            ? 'A GNSS upgrade is already running'
                            : 'Send a GNSS receiver firmware upgrade command'
                      }
                    >
                      <Satellite size={17} />
                      <span>
                        <strong>Upgrade GNSS firmware</strong>
                        <small>
                          {gnssUpgrading
                            ? `Upgrade in progress: ${gnssUpgrading}`
                            : `Flash the receiver from the cloud firmware library${hubGnssType ? ` (${hubGnssType})` : ''}.`}
                        </small>
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className={styles.canControls}>
              <div className={styles.commandGroup}>
                <span className={styles.groupLabel}>CAN bus power</span>
                {[0, 1].map(port => {
                  const powered = isCanPortPowered(port);
                  return (
                    <div className={styles.canRow} key={port}>
                      <span>
                        <strong>CAN {port}</strong>
                        <small>{powered ? 'Power is on' : 'Power is off'}</small>
                      </span>
                      <span
                        className={`${styles.powerState} ${powered ? styles.powerOn : styles.powerOff}`}
                      >
                        <i />
                        {powered ? 'On' : 'Off'}
                      </span>
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            requestCommand(
                              `canbus_${port}_power_on`,
                              `Power on CAN ${port}?`,
                              'Connected sensors will receive power immediately.'
                            )
                          }
                        >
                          Turn on
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            requestCommand(
                              `canbus_${port}_power_off`,
                              `Power off CAN ${port}?`,
                              'Connected sensors on this bus will stop reporting.'
                            )
                          }
                        >
                          Turn off
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={styles.commandGroup}>
                <span className={`${styles.groupLabel} ${styles.groupLabelSpaced}`}>
                  CAN bus TX/RX
                </span>
                <form className={styles.canLogCard} onSubmit={handleStartCanLog}>
                  <span>
                    <strong>Log CAN frames (RX)</strong>
                  </span>
                  <div className={styles.canLogForm}>
                    <label>
                      <span>Bus</span>
                      <select value={canLogBus} onChange={e => setCanLogBus(e.target.value)}>
                        {CAN_LOG_BUSES.map(port => (
                          <option key={port} value={port}>
                            CAN {port}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Seconds</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={CAN_LOG_MIN_SECONDS}
                        max={CAN_LOG_MAX_SECONDS}
                        step={1}
                        value={canLogSeconds}
                        onChange={e => setCanLogSeconds(e.target.value)}
                      />
                    </label>
                    <button type="submit">Start log</button>
                  </div>
                </form>
                <div className={styles.canLogCard}>
                  <span>
                    <strong>Send CAN frames (TX)</strong>
                  </span>
                  <div className={`${styles.canLogForm} ${styles.canTxForm}`}>
                    <label>
                      <span>Bus</span>
                      <select value={canTxBus} onChange={e => setCanTxBus(e.target.value)}>
                        {CAN_LOG_BUSES.map(port => (
                          <option key={port} value={port}>
                            CAN {port}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCanTxError('');
                        setCanTxOpen(true);
                      }}
                    >
                      Send CAN frames
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {showIncidents && (
          <aside className={styles.sidePanel}>
            <div className={styles.commandHeader}>
              <div>
                <span>Hub events</span>
                <h2>Unsolved incidents</h2>
              </div>
              <span className={`${styles.panelCount} ${styles.incidentCount}`}>
                {unsolvedIncidentCount}
              </span>
            </div>
            <div className={styles.sidePanelList}>
              {incidents.map(incident => {
                const createdAt = Date.parse(incident.created_at) / 1000;
                return (
                  <div
                    className={`${styles.sidePanelItem} ${styles.incidentItem}`}
                    key={incident.id}
                  >
                    <i />
                    <div>
                      <strong>{incident.error_type}</strong>
                      <p>{incident.error_message}</p>
                      <time title={formatTimestamp(createdAt)}>
                        {formatRelativeTime(createdAt)} ({formatLocalDateTime(createdAt)})
                        {incident.status === 'taken' && incident.owner
                          ? ` · ${incident.owner}`
                          : ''}
                      </time>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.incidentActions}>
              {isAdmin() && (
                <button
                  type="button"
                  className={styles.solveAllButton}
                  onClick={() => setConfirmSolveAll(true)}
                  disabled={solvingAll}
                >
                  <CheckCheck size={15} />
                  Resolve all incidents
                </button>
              )}
              <Link to={`/machine/${machineId}/incidents`}>View all incidents</Link>
            </div>
          </aside>
        )}

        <aside className={`${styles.sidePanel} ${showIncidents ? '' : styles.sidePanelWide}`}>
          <div className={styles.commandHeader}>
            <div>
              <span>Hub events</span>
              <h2>Notifications</h2>
            </div>
            {notificationPagination?.total_count > 0 && (
              <span className={styles.panelCount}>{notificationPagination.total_count}</span>
            )}
          </div>
          <div className={styles.sidePanelList}>
            {notifications === null ? (
              <div className={styles.sidePanelEmpty}>Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className={styles.sidePanelEmpty}>
                <Bell size={18} />
                No notifications for this hub
              </div>
            ) : (
              notifications.map((notification, index) => {
                const time = parseInt(notification.time, 10);
                return (
                  <div className={styles.sidePanelItem} key={`${notification.time}-${index}`}>
                    <i />
                    <div>
                      <p>{notification.text}</p>
                      <time title={formatTimestamp(time)}>{formatRelativeTime(time)}</time>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {notificationPagination?.total_pages > 1 && (
            <div className={styles.sidePanelPager}>
              <button
                type="button"
                aria-label="Newer notifications"
                onClick={() => setNotificationPage(page => page - 1)}
                disabled={!notificationPagination.has_prev}
              >
                <ChevronLeft size={15} />
              </button>
              <span>
                {notificationPagination.page} / {notificationPagination.total_pages}
              </span>
              <button
                type="button"
                aria-label="Older notifications"
                onClick={() => setNotificationPage(page => page + 1)}
                disabled={!notificationPagination.has_next}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={confirmSolveAll}
        title="Resolve all incidents for this hub?"
        description={`This marks ${unsolvedIncidentCount} unowned or in-progress incidents as solved.`}
        confirmLabel="Resolve all"
        tone="primary"
        busy={solvingAll}
        onConfirm={handleSolveAllIncidents}
        onCancel={() => setConfirmSolveAll(false)}
      />

      <ConfirmDialog
        open={!!pendingCommand}
        title={pendingCommand?.title}
        description={pendingCommand?.description}
        confirmLabel="Confirm command"
        onConfirm={confirmCommand}
        onCancel={() => setPendingCommand(null)}
      />

      <ActionDrawer
        open={canTxOpen}
        eyebrow={`CAN ${canTxBus} · ${selectedDevice}`}
        title="Send CAN frames"
        description="One CAN string per line. Edit the data bytes, then fix the checksum before sending."
        onClose={closeCanTx}
        closeDisabled={canTxSending}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeCanTx}
              disabled={canTxSending}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSendCanFrames}
              disabled={
                canTxSending || canTxParsed.frames.length === 0 || canTxParsed.errors.length > 0
              }
            >
              {canTxSending ? 'Sending…' : `Send to CAN ${canTxBus}`}
            </button>
          </>
        }
      >
        <div className={styles.canTxEditor}>
          {canTxError && <div className={styles.formError}>{canTxError}</div>}
          <label>
            <span>CAN strings</span>
            <textarea
              value={canTxText}
              onChange={e => setCanTxText(e.target.value)}
              placeholder={CAN_TX_PLACEHOLDER}
              rows={12}
              spellCheck={false}
            />
          </label>
          <div className={styles.canTxTools}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleFixCanCrc}
              disabled={!canTxText.trim()}
            >
              Fix CRC
            </button>
            <small>
              {canTxParsed.frames.length} valid frame{canTxParsed.frames.length === 1 ? '' : 's'}
              {canTxParsed.errors.length > 0 && (
                <span className={styles.canTxErrors}>
                  {' · '}
                  {canTxParsed.errors.length} invalid line
                  {canTxParsed.errors.length === 1 ? '' : 's'} (line {canTxParsed.errors[0].line}:{' '}
                  {canTxParsed.errors[0].message})
                </span>
              )}
            </small>
          </div>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={gnssUpgradeOpen}
        eyebrow={`GNSS receiver · ${selectedDevice}`}
        title="Upgrade GNSS firmware"
        description="The hub downloads the firmware from the cloud and streams it straight into the GNSS receiver."
        onClose={closeGnssUpgrade}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeGnssUpgrade}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={confirmGnssUpgrade}
              disabled={!hubGnssType || !selectedGnssFirmware || !isDeviceOnline}
            >
              Send upgrade
            </button>
          </>
        }
      >
        <div className={styles.gnssUpgradeForm}>
          <dl className={styles.gnssUpgradeSummary}>
            <div>
              <dt>Detected GNSS type</dt>
              <dd>{hubGnssType || 'Unknown'}</dd>
            </div>
            <div>
              <dt>Current firmware</dt>
              <dd>{parsedData?.gnss_version || '—'}</dd>
            </div>
          </dl>
          {!hubGnssType ? (
            <div className={styles.formError}>
              The hub has not reported a recognised GNSS receiver (<code>gnss_version</code>), so no
              firmware can be selected. Wait for a status update from the hub and try again.
            </div>
          ) : (
            <>
              {gnssFirmwareError && <div className={styles.formError}>{gnssFirmwareError}</div>}
              <label>
                <span>Firmware version</span>
                <select
                  value={gnssUpgradeId}
                  onChange={event => setGnssUpgradeId(event.target.value)}
                  disabled={!gnssFirmwares || gnssFirmwares.length === 0}
                >
                  <option value="">
                    {gnssFirmwares === null
                      ? 'Loading firmware…'
                      : gnssFirmwares.length === 0
                        ? `No ${hubGnssType} firmware uploaded`
                        : 'Select a version'}
                  </option>
                  {(gnssFirmwares || []).map(firmware => (
                    <option key={firmware.id} value={String(firmware.id)}>
                      {firmware.version} · {formatFirmwareSize(firmware.size)}
                      {firmware.comment ? ` · ${firmware.comment}` : ''}
                    </option>
                  ))}
                </select>
                {gnssFirmwares?.length === 0 && !gnssFirmwareError && (
                  <small>
                    Upload {hubGnssType} firmware under <Link to="/gnss-ota">GNSS firmware</Link>{' '}
                    first.
                  </small>
                )}
              </label>
              {selectedGnssFirmware && (
                <div className={styles.gnssCommandPreview}>
                  <span>
                    Command{' '}
                    <code>
                      {buildGnssUpgradeCommand(
                        selectedGnssFirmware.gnss_type,
                        selectedGnssFirmware.version
                      )}
                    </code>{' '}
                    will be sent to {selectedDevice}.
                  </span>
                  <small>
                    {formatFirmwareSize(selectedGnssFirmware.size)} · estimated transfer time ~
                    {estimateGnssUpgradeMinutes(selectedGnssFirmware.size)} min
                  </small>
                </div>
              )}
            </>
          )}
          <div className={styles.gnssUpgradeWarning}>
            <strong>Before you start</strong>
            <ul>
              <li>NTRIP / RTK corrections stop for the whole duration of the upgrade.</li>
              <li>
                The file is sent to the receiver at 115200 baud, roughly 1.5 minutes per MB (a 30 MB
                file takes about 45 minutes).
              </li>
              <li>The GNSS receiver and the hub must stay powered until the upgrade finishes.</li>
              <li>The hub reboots automatically when the upgrade is finished.</li>
            </ul>
          </div>
        </div>
      </ActionDrawer>

      <nav className={styles.viewSwitcher} aria-label="Hub status sections">
        {statusViews.map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              type="button"
              className={activeView === view.id ? styles.viewActive : ''}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={16} />
              <span>{view.label}</span>
            </button>
          );
        })}
      </nav>

      {activeView === 'overview' && (
        <>
          <section className={styles.panel}>
            <div className={styles.statusToolbar}>
              {parsedData && (
                <StatusCard.OnlineIndicator
                  isOnline={isDeviceOnline}
                  lastSyncTime={parsedData?.last_update_time}
                  inOperation={parsedData?.canbus_clients > 0 && parsedData?.gnss_clients > 0}
                  batteryPowered={parsedData?.is_battery_powered === true}
                />
              )}
              <label className={styles.timestampField}>
                <span>Snapshot</span>
                <select
                  value={selectedStatusTimestamp}
                  onChange={event => handleStatusTimestampChange(event.target.value)}
                >
                  <option value="latest">
                    Latest
                    {statusTimestamps.length > 0
                      ? ` · ${formatTimestamp(statusTimestamps[0])}`
                      : ''}
                  </option>
                  {statusTimestamps.map(timestamp => (
                    <option key={timestamp} value={String(timestamp)}>
                      {formatTimestamp(timestamp)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {parsedData ? (
              <div className={styles.statusGrid}>
                <StatusCard.Grid>{renderStatusCards()}</StatusCard.Grid>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Activity size={26} />
                <strong>No status data</strong>
                <p>The hub has not reported a status snapshot.</p>
              </div>
            )}
          </section>
          {parsedData?.imus && Object.keys(parsedData.imus).length > 0 && (
            <ImuDiagnosticsPanel
              imus={parsedData.imus}
              imuInstallationsByCan={imuInstallationsByCan}
              isDeviceOnline={isDeviceOnline}
              onSendCommand={handleSendCommand}
            />
          )}
        </>
      )}

      {activeView === 'system' && (
        <div className={styles.diagnosticsGrid}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div>
                <Database size={17} />
                <span>
                  <strong>RAM heap</strong>
                  <small>Current memory allocator snapshot</small>
                </span>
              </div>
            </div>
            {memInfo ? (
              <pre
                className={`${styles.diagnosticPre} ${!isDeviceOnline ? styles.offlineData : ''}`}
              >
                {formatMemInfo(memInfo)}
              </pre>
            ) : (
              <div className={styles.emptyState}>
                <Database size={24} />
                <strong>No memory data</strong>
              </div>
            )}
          </section>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div>
                <Cpu size={17} />
                <span>
                  <strong>Tasks &amp; CPU</strong>
                  <small>Runtime task utilization</small>
                </span>
              </div>
              {taskInfo && (
                <select
                  value={selectedTaskInfoTimestamp}
                  onChange={event => setSelectedTaskInfoTimestamp(event.target.value)}
                >
                  {getTaskInfoTimestamps(taskInfo).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedTaskInfoTimestamp && taskInfo?.[selectedTaskInfoTimestamp] ? (
              <pre
                className={`${styles.diagnosticPre} ${!isDeviceOnline ? styles.offlineData : ''}`}
              >
                {formatTaskInfoText(taskInfo[selectedTaskInfoTimestamp])}
              </pre>
            ) : (
              <div className={styles.emptyState}>
                <Cpu size={24} />
                <strong>No task data</strong>
              </div>
            )}
          </section>
        </div>
      )}

      {activeView === 'bluetooth' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <Bluetooth size={17} />
              <span>
                <strong>Paired Bluetooth devices</strong>
                <small>Authentication and connection state</small>
              </span>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() =>
                requestCommand(
                  'ble_pairing_mode',
                  'Enable pairing mode?',
                  'The hub will accept new Bluetooth pairings for 120 seconds.'
                )
              }
              disabled={!isDeviceOnline}
            >
              Start pairing mode
            </button>
          </div>
          {bluetoothInfo?.devices?.length ? (
            <div className={styles.bluetoothGrid}>
              {bluetoothInfo.devices.map(device => (
                <article key={`${device.address}-${device.type}`}>
                  <div>
                    <strong>{device.address}</strong>
                    <small>{device.type}</small>
                  </div>
                  <dl>
                    <div>
                      <dt>Connected</dt>
                      <dd>{device.connected ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>Authenticated</dt>
                      <dd>{device.authenticated ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>Secure</dt>
                      <dd>{device.secure_connection ? 'Yes' : 'No'}</dd>
                    </div>
                  </dl>
                  <button type="button" onClick={() => handleUnpairDevice(device)}>
                    Unpair device
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Bluetooth size={26} />
              <strong>No paired devices</strong>
              <p>Start pairing mode to connect a Bluetooth device.</p>
            </div>
          )}
        </section>
      )}

      {activeView === 'network' && (
        <NetworkUsagePanel serialNumber={selectedDevice} refreshToken={refreshToken} />
      )}

      {activeView === 'raw' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <Braces size={17} />
              <span>
                <strong>Raw status payload</strong>
                <small>Unformatted data for support and engineering</small>
              </span>
            </div>
          </div>
          {parsedData ? (
            <pre className={`${styles.rawPre} ${!isDeviceOnline ? styles.offlineData : ''}`}>
              {statusData}
            </pre>
          ) : (
            <div className={styles.emptyState}>
              <Braces size={25} />
              <strong>No raw data</strong>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default DeviceStatus;
